import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { FilterType, GetMessageQuery as GetMessage } from '__generated__/graphql';

import { InfoTooltip } from '../../../common/UI/InfoTooltip';
import {
  getOrderFiltersInput, getParticipantFiltersInput, getPurchaseFiltersInput, getRegistrationFiltersInput,
} from '../../../common/segmentHelpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ContactFilters, {
  ContactFilterParams, defaultFilters as defaultContactFilters,
} from '../../Participants/Contacts/ContactFilters';
import EditMessageMutation from './EditMessageMutation';
import OrderFilters, {
  OrderFilterParams, defaultFilters as defaultOrderFilters,
} from '../../Participants/Orders/OrderFilters';
import RegistrationFilters, {
  RegistrationFilterParams, defaultFilters as defaultRegistrationFilters,
} from '../../Participants/Registrations/RegistrationFilters';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useScroll from '../../../common/useScroll';

type Message = GetMessage['project']['message'];

export interface EditMessageAudienceFormProps {
  message: Message;
}

const EditMessageAudienceForm = ({ message }: EditMessageAudienceFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const getValues = (message: Message) => ({
    filter_type: message.segment_type,
    filters: {
      ...message.segment_type === FilterType.registrations
        && getRegistrationFiltersInput(message.segment.participant_filters?.registration_filters || {}),
      ...message.segment_type === FilterType.orders && {
        ...getPurchaseFiltersInput(message.segment.participant_filters?.order_filters?.purchase_filters || {}),
        ...getOrderFiltersInput(message.segment.participant_filters?.order_filters || {}),
      },
      ...getParticipantFiltersInput(message.segment.participant_filters || {}),
    },
  });

  const form = useForm({
    values: getValues(message),
  });

  useNavigationPrompt(form.changed());

  const getFiltersInput = () => {
    const { filters } = form.values;

    if (form.values.filter_type === FilterType.registrations) {
      return {
        locales: filters.locales,
        mail_opt_ins: filters.mail_opt_ins,
        registration_filters: {
          ticket_categories: filters.ticket_categories,
          tickets: filters.tickets,
          time_slots: filters.time_slots,
          ticket_number_sequences: filters.ticket_number_sequences,
          has_ticket_number: filters.has_ticket_number,
          has_team: filters.has_team,
          products: filters.products,
          product_variants: filters.product_variants,
          promotions: filters.promotions,
          participant_fields: filters.participant_fields,
          participant_field_choices: filters.participant_field_choices,
          invitations: filters.invitations,
          coupons: filters.coupons,
          lotteries: filters.lotteries,
          confirmed_lotteries: filters.confirmed_lotteries,
          rejected_lotteries: filters.rejected_lotteries,
          statuses: filters.statuses,
        },
      };
    }

    if (form.values.filter_type === FilterType.orders) {
      return {
        locales: filters.locales,
        mail_opt_ins: filters.mail_opt_ins,
        order_filters: {
          participant_fields: filters.participant_fields,
          participant_field_choices: filters.participant_field_choices,
          purchase_filters: {
            ticket_categories: filters.ticket_categories,
            tickets: filters.tickets,
            time_slots: filters.time_slots,
            products: filters.products,
            product_variants: filters.product_variants,
            promotions: filters.promotions,
            invitations: filters.invitations,
            coupons: filters.coupons,
            lotteries: filters.lotteries,
            confirmed_lotteries: filters.confirmed_lotteries,
            rejected_lotteries: filters.rejected_lotteries,
            assigned: filters.assigned,
            payment_statuses: filters.payment_statuses,
          },
        },
      };
    }

    if (form.values.filter_type === FilterType.participants) {
      return {
        locales: filters.locales,
        mail_opt_ins: filters.mail_opt_ins,
      };
    }

    return {};
  };

  const [editMessage, { error, loading }] = useMutation(EditMessageMutation, {
    variables: {
      input: {
        id: message.id,
        filter_type: form.values.filter_type,
        filters: getFiltersInput(),
      },
    },
    onCompleted: ({ editMessage: message }) => {
      notifier.success(t('notifications.message_saved'));

      form.reset(getValues(message));
    },
    onError: () => {
      scrollTo('MessageTab');
    },
  });

  const filters = {
    ...form.values.filters,
    event: message.event.id,
  };

  const handleRegistrationFiltersChange = (update: (filters: RegistrationFilterParams) => RegistrationFilterParams) => {
    form.set((values) => {
      const filters = update(values.filters);

      return {
        ...values,
        filters: {
          ticket_categories: filters.ticket_categories || [],
          tickets: filters.tickets || [],
          time_slots: filters.time_slots || [],
          ticket_number_sequences: filters.ticket_number_sequences || [],
          has_ticket_number: filters.has_ticket_number,
          has_team: filters.has_team,
          products: filters.products || [],
          product_variants: filters.product_variants || [],
          promotions: filters.promotions || [],
          participant_fields: filters.participant_fields || [],
          participant_field_choices: filters.participant_field_choices || [],
          invitations: filters.invitations || [],
          coupons: filters.coupons || [],
          lotteries: filters.lotteries || [],
          confirmed_lotteries: filters.confirmed_lotteries || [],
          rejected_lotteries: filters.rejected_lotteries || [],
          statuses: filters.statuses || [],
          locales: filters.locales || [],
          mail_opt_ins: filters.mail_opt_ins || [],
        },
      };
    });
  };

  const handleOrderFiltersChange = (update: (filters: OrderFilterParams) => OrderFilterParams) => {
    form.set((values) => {
      const filters = update(values.filters);

      return {
        ...values,
        filters: {
          ticket_categories: filters.ticket_categories || [],
          tickets: filters.tickets || [],
          time_slots: filters.time_slots || [],
          products: filters.products || [],
          product_variants: filters.product_variants || [],
          promotions: filters.promotions || [],
          participant_fields: filters.participant_fields || [],
          participant_field_choices: filters.participant_field_choices || [],
          invitations: filters.invitations || [],
          coupons: filters.coupons || [],
          lotteries: filters.lotteries || [],
          confirmed_lotteries: filters.confirmed_lotteries || [],
          rejected_lotteries: filters.rejected_lotteries || [],
          assigned: filters.assigned,
          payment_statuses: filters.payment_statuses,
          locales: filters.locales || [],
          mail_opt_ins: filters.mail_opt_ins || [],
        },
      };
    });
  };

  const handleContactFiltersChange = (update: (filters: ContactFilterParams) => ContactFilterParams) => {
    form.set((values) => {
      const filters = update(values.filters);

      return {
        ...values,
        filters: {
          mail_opt_ins: filters.mail_opt_ins || [],
          locales: filters.locales || [],
        },
      };
    });
  };

  return (
    <>
      <UI.Div sc={{ mb: 4 }}>
        <UI.SectionHeading>
          {t('audience')}
        </UI.SectionHeading>
        <UI.InputDescription>
          {t('message_audience_description')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Card>
        <UI.Form onSubmit={() => editMessage()}>
          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.InputGroup>
              <UI.InputLabel>
                {t('send_message_to')}
                {' '}
                <InfoTooltip>
                  <UI.GridContainer>
                    <UI.H4>
                      {t('audience')}
                    </UI.H4>

                    <UI.Div>
                      <UI.Strong>
                        {t('participants')}
                      </UI.Strong>
                      <UI.Paragraph>
                        {t('message_audience_participants_description')}
                      </UI.Paragraph>
                      <UI.Strong>
                        {t('buyers')}
                      </UI.Strong>
                      <UI.Paragraph>
                        {t('message_audience_buyers_description')}
                      </UI.Paragraph>
                      <UI.Strong>
                        {t('all_contacts')}
                      </UI.Strong>
                      <UI.Paragraph sc={{ mb: 0 }}>
                        {t('message_audience_all_contacts_description')}
                      </UI.Paragraph>
                    </UI.Div>
                  </UI.GridContainer>
                </InfoTooltip>
              </UI.InputLabel>
              <UI.RadioPill>
                <UI.Radio
                  {...form.radio('filter_type', FilterType.registrations, {
                    onChange: () => handleRegistrationFiltersChange((filters) => ({
                      ...defaultRegistrationFilters,
                      ...filters,
                    })),
                  })}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('participants')}
                </UI.Radio>
                <UI.Radio
                  {...form.radio('filter_type', FilterType.orders, {
                    onChange: () => handleOrderFiltersChange((filters) => ({
                      ...defaultOrderFilters,
                      ...filters,
                    })),
                  })}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('buyers')}
                </UI.Radio>
                <UI.Radio
                  {...form.radio('filter_type', FilterType.participants, {
                    onChange: () => handleContactFiltersChange((filters) => ({
                      ...defaultContactFilters,
                      ...filters,
                    })),
                  })}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('all_contacts')}
                </UI.Radio>
              </UI.RadioPill>
            </UI.InputGroup>

            <UI.Legend>
              {t('criteria')}
            </UI.Legend>

            {form.get('filter_type') === FilterType.registrations && (
              <RegistrationFilters
                filters={filters}
                onChange={handleRegistrationFiltersChange}
                types={[
                  'tickets', 'ticket_number_sequences', 'products', 'participant_fields', 'invitations', 'coupons',
                  'lotteries', 'confirmed_lotteries', 'rejected_lotteries', 'statuses', 'team', 'mail_opt_ins',
                  'locales',
                ]}
                showTotals={false}
                showResetButton={false}
              />
            )}

            {form.get('filter_type') === FilterType.orders && (
              <OrderFilters
                filters={filters}
                onChange={handleOrderFiltersChange}
                types={[
                  'tickets', 'products', 'participant_fields', 'invitations', 'coupons', 'lotteries',
                  'confirmed_lotteries', 'rejected_lotteries', 'assigned', 'payment_statuses', 'mail_opt_ins',
                  'locales',
                ]}
                showTotals={false}
                showResetButton={false}
              />
            )}

            {form.get('filter_type') === FilterType.participants && (
              <ContactFilters
                filters={filters}
                onChange={handleContactFiltersChange}
                types={['mail_opt_ins', 'locales']}
                showTotals={false}
                showResetButton={false}
              />
            )}

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save_message')}
              loading={loading}
            />
          </UI.FormGrid>
        </UI.Form>
      </UI.Card>
    </>
  );
};

export default EditMessageAudienceForm;
